/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { isMobile } from "react-device-detect";

import { ImageFit } from "@vienna/common";
import {
  BaseBlockSnapshot,
  BlockType,
  FileStatus,
  ImageBlockSnapshot,
} from "@vienna/common/src/entities/block-snapshot.entity";

import { Size } from "../../plate/lib/types";
import { BaseBlock } from "src/classes/block/BaseBlock";
import { FileHandlingMethods } from "src/classes/block/block.interfaces";
import { PlateSocket } from "src/classes/plate/PlateSocket/PlateSocket";
import { ClipboardUtils } from "src/classes/utilities/ClipboardUtils";
import { FileUtils } from "src/classes/utilities/FileUtils";
import { MathUtils } from "src/classes/utilities/MathUtils";
export class ImageBlock extends BaseBlock<ImageBlockSnapshot> implements FileHandlingMethods {
  private static cdnURL = `https://vienna-${process.env.ENVIRONMENT}.imgix.net`;

  private static imageBucketName = `${process.env.ENVIRONMENT}-vht-image-bucket`;

  private static imageBucketURL = `https://${ImageBlock.imageBucketName}.s3.amazonaws.com`;

  constructor(snapshot: ImageBlockSnapshot, plateSocket: PlateSocket | null) {
    super(snapshot, plateSocket);
  }

  /* -------------------------------------------------- */
  /*                      Methods                       */
  /* -------------------------------------------------- */
  public changeImageFit(): void {
    if (!this.plateSocket) return;
    const imageFit = this.snapshot.imageFit === ImageFit.Contain ? ImageFit.Cover : ImageFit.Contain;
    this.setState({ imageFit });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  public createCaption(): void {
    this.setState({ hasCaption: true });
  }

  public removeCaption(): void {
    this.setState({ hasCaption: false });
    this.updateCaption("");
  }

  public updateCaption(caption: string): void {
    if (!this.plateSocket) return;
    this.setState({ caption, hasCaption: !!caption });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  public downloadImage(): void {
    if (!this.downloadLink) return;
    window.location.assign(this.downloadLink);
  }

  public copyImage(): void {
    if (!this.baseImageLink) return;
    ClipboardUtils.copyImageToClipboard(this.baseImageLink);
  }

  /* -------------------------------------------------- */
  /*                     File Upload                    */
  /* -------------------------------------------------- */
  public addFile(file: File, plateSize?: Size): void {
    if (!file) return;
    if (!this.plateSocket) return;
    const renamedFile = FileUtils.renameFile(file, this.snapshot.id);
    this.setState({
      filename: renamedFile.name,
      originalFilename: file.name,
      filesize: renamedFile.size,
      fileStatus: FileStatus.Uploading,
    });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
    FileUtils.uploadFileWithStreaming(renamedFile, ImageBlock.imageBucketName, this.monitorUploadProgress);
    if (!plateSize) return; //@todo throw error
    this.resizeFromImageUpload(file, plateSize);
  }

  /* ------------------ Must be arrow function to be bound as streaming callback ------------------ */
  public monitorUploadProgress = (fileStatus: FileStatus, uploadPercent?: number): void => {
    if (!this.plateSocket) return;
    this.setState({ fileStatus, uploadPercent: uploadPercent || this.snapshot.uploadPercent });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  };

  private async resizeFromImageUpload(imageFile: File, plateSize: Size): Promise<void> {
    const fileSizeInPixels = await FileUtils.getPixelSizeOfImageFile(imageFile);
    if (!fileSizeInPixels) return;
    const maxTop = plateSize.height - fileSizeInPixels.height;
    const maxLeft = plateSize.width - fileSizeInPixels.width;

    // if resizing would result in bounds violation, move block
    if (this.snapshot.top > maxTop || this.snapshot.left > maxLeft) {
      const newLeft = Math.min(this.snapshot.left, maxLeft);
      const newTop = Math.min(this.snapshot.top, maxTop);
      this.move(newTop, newLeft);
    }
    this.resize(fileSizeInPixels.width, fileSizeInPixels.height);
  }

  /* -------------------------------------------------- */
  /*                     Image URL                      */
  /* -------------------------------------------------- */
  public getImageLinkForSize(size: "standard" | "biggest" | "dynamic"): string {
    return `${this.baseImageLink}?${this.getImageParamsForSize(size)}`;
  }

  private getImageParamsForSize(size: "standard" | "biggest" | "dynamic"): string {
    if (this.isGif) return "";
    switch (size) {
      case "standard":
        return "fit=max&w=500";
      case "biggest":
        return "auto=format";
      case "dynamic":
        const scaleFactor = isMobile ? 0.5 : 2;
        const imageHeight = MathUtils.roundNumToNearest(this.snapshot.height * scaleFactor, 150);
        const imageWidth = MathUtils.roundNumToNearest(this.snapshot.width * scaleFactor, 150);
        return `fit=max&h=${imageHeight}&w=${imageWidth}`;
    }
  }

  public get baseImageLink(): string {
    return `${ImageBlock.cdnURL}/${this.imageURIComponent}`;
  }

  public get fallbackImageLink(): string {
    return `${ImageBlock.imageBucketURL}/${this.imageURIComponent}`;
  }

  public get downloadLink(): string | null {
    if (!this.baseImageLink) return null;
    if (!this.snapshot.filename) return null;
    return `${this.baseImageLink}?dl=${this.imageURIComponent}`;
  }

  public get searchLink(): string | null {
    return `https://www.bing.com/images/search?view=detailv2&iss=sbi&form=SBIIRP&sbisrc=ImgDropper&q=imgurl:${this.baseImageLink}`;
  }

  private get imageURIComponent(): string {
    return encodeURIComponent(`${this.snapshot.filename}`);
  }

  /* -------------------------------------------------- */
  /*                  Image Extension                   */
  /* -------------------------------------------------- */

  private get extension(): string {
    if (!this.snapshot.filename) return "";
    if (!this.snapshot.originalFilename) return "";
    const filename = this.snapshot.filename;
    const originalFilename = this.snapshot.originalFilename;
    const extension = filename.split(".").pop() || null;
    const fallbackExtension = originalFilename.split(".").pop() || null;
    if (!extension && !fallbackExtension) {
      throw new Error(`The image with filename ${this.snapshot.filename} has no extension, which is a problem.`);
    }
    return extension || fallbackExtension || "";
  }

  private get isGif(): boolean {
    return this.extension === "gif";
  }

  /* -------------------------------------------------- */
  /*                       Create                       */
  /* -------------------------------------------------- */
  public static newSnapshot(baseSnapshot: BaseBlockSnapshot): ImageBlockSnapshot {
    const snapshot: ImageBlockSnapshot = {
      ...baseSnapshot,
      type: BlockType.Image,
      imageFit: ImageFit.Cover,
      caption: null,
      hasCaption: false,
      tags: [],
      filename: null,
      filesize: null,
      originalFilename: null,
      fileStatus: FileStatus.None,
      uploadPercent: 0,
    };
    return snapshot;
  }
}
